import { type MedplumClient } from '@medplum/core'
import { getGraphQLErrorMessage } from 'utils/helpers'

/** Maximum `_count` page size accepted by Medplum list queries. */
export const MEDPLUM_LIST_PAGE_SIZE = 1000

/** Maximum `_offset` accepted by Medplum for offset-based pagination. */
export const MEDPLUM_LIST_MAX_OFFSET = 10000

interface FetchAllListPagesParams {
  medplum: MedplumClient
  query: string
  listKey: string
}

/**
 * Reads every page of a GraphQL list query by walking `_offset` until a short
 * page is returned or `MEDPLUM_LIST_MAX_OFFSET` is reached.
 *
 * The query must declare an `$offset: Int` variable and request
 * `_count: 1000` with a `_sort`, so paging returns a stable ordering.
 *
 * @param listKey - Name of the list field to read from the response, e.g. `OrganizationList`.
 */
export async function fetchAllListPages<T>({
  medplum,
  query,
  listKey
}: FetchAllListPagesParams): Promise<T[]> {
  const resources: T[] = []

  for (let offset = 0; offset <= MEDPLUM_LIST_MAX_OFFSET; offset += MEDPLUM_LIST_PAGE_SIZE) {
    const { data, errors }: { data: Record<string, T[] | null | undefined>; errors: unknown } =
      await medplum.graphql(query, null, { offset })

    if (!data && errors) {
      throw new Error(
        `A Graphql query error is returned from Medplum ${getGraphQLErrorMessage(errors)}`
      )
    }

    const page = data?.[listKey] ?? []
    resources.push(...page)

    if (page.length < MEDPLUM_LIST_PAGE_SIZE) break
  }

  return resources
}
